<template>
  <section id="health-plan" class="card">
    <header class="card__head">
      <span class="step">4</span>
      <div>
        <strong>健康方案与任务</strong>
        <p>选择已发布模板发放方案，查看执行进度和当天任务</p>
      </div>
      <em>{{ running ? '方案执行中' : '暂无执行中方案' }}</em>
    </header>
    <div v-loading="loading" class="plan">
      <div class="plan-card">
        <p>当前方案</p>
        <strong>{{ currentName }}</strong>
        <span>{{ currentDesc }}</span>
        <div class="plan-progress">
          <div>
            <span>完成进度</span>
            <span>{{ progress.percent || 0 }}%</span>
          </div>
          <i><b :style="{ width: (progress.percent || 0) + '%' }"></b></i>
        </div>
        <div class="plan-actions">
          <button type="button" class="primary is-light" @click="openIssue">发放方案</button>
          <button v-if="running" type="button" class="primary is-light" @click="terminate">终止方案</button>
        </div>
      </div>
      <div class="tasks">
        <div class="tasks__head">
          <div>
            <strong>今日任务</strong>
            <p>今日 {{ progress.todayFinished || 0 }}/{{ progress.todayTotal || todayTasks.length }}，发放后当天立即展开</p>
          </div>
        </div>
        <div v-if="todayTasks.length" class="task-grid">
          <div v-for="task in todayTasks" :key="task.id" class="task" :class="{ 'is-on': task.status == 1 }">
            <span class="check">
              <i v-if="task.status == 1" class="el-icon-check"></i>
            </span>
            <span class="task__copy">
              <strong>{{ task.taskName }}</strong>
              <em>{{ taskTypeText(task.taskType) }} · {{ task.planTime || '--' }}</em>
            </span>
            <span class="task__cat">{{ taskStatusText(task.status) }}</span>
          </div>
        </div>
        <div v-else class="med-empty">今天还没有任务。方案开始日在今天及以前时，发放后会立即展开当天任务。</div>
      </div>
    </div>

    <el-dialog title="发放健康方案" :visible.sync="issueVisible" width="640px" append-to-body @close="resetIssue">
      <el-form label-width="88px" size="small">
        <el-form-item label="方案模板">
          <el-select v-model="issueForm.templateId" placeholder="请选择已发布模板" style="width: 100%;" @change="loadTemplateItems">
            <el-option v-for="item in templates" :key="item.id" :label="item.schemeName" :value="item.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="开始日期">
          <el-date-picker v-model="issueForm.startDate" type="date" value-format="yyyy-MM-dd" placeholder="不选则从今天开始" style="width: 100%;" />
        </el-form-item>
        <el-form-item label="任务项">
          <div v-if="templateItems.length" class="issue-items">
            <el-checkbox v-model="checkAll" @change="toggleAll">全选</el-checkbox>
            <el-checkbox-group v-model="issueForm.itemIds">
              <el-checkbox v-for="item in templateItems" :key="item.id" :label="item.id">
                {{ item.taskName }}
                <em class="issue-item-meta">{{ taskTypeText(item.taskType) }} · {{ item.remindTimes || '无提醒' }}</em>
              </el-checkbox>
            </el-checkbox-group>
          </div>
          <span v-else class="empty-tip">选择模板后展示可勾选任务，不勾选则发放全部启用任务项</span>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button size="small" @click="issueVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="issuing" @click="submitIssue">确认发放</el-button>
      </span>
    </el-dialog>
  </section>
</template>

<script>
import {
  listAvailableSchemeTemplates,
  getSchemeTemplateDetail,
  issueHealthScheme,
  terminateHealthScheme,
  listMemberSchemes,
  getSchemeProgress,
  listTodaySchemeTasks
} from '@/api/healthManage/index';

const SCHEME_STATUS = { 1: '执行中', 2: '已完成', 3: '已终止' };
const TASK_STATUS = { 0: '待完成', 1: '已完成', 2: '已过期' };
const TASK_TYPE = { 1: '打卡', 2: '用药提醒', 3: '测血压', 4: '测血糖', 5: '健康饮食', 6: '运动建议', 7: '门店复测' };

/**
 * 健康方案（健管端）
 * 列表、进度、发放、终止走方案实例接口；今日任务只展示，打卡在会员端完成
 */
export default {
  name: 'HealthScheme',
  props: {
    member: { type: Object, required: true }
  },
  data() {
    return {
      loading: false,
      schemes: [],
      progress: {},
      todayTasks: [],
      issueVisible: false,
      issuing: false,
      templates: [],
      templateItems: [],
      checkAll: true,
      issueForm: { templateId: null, startDate: '', itemIds: [] }
    };
  },
  computed: {
    /** 列表已按执行中优先，取第一条执行中方案 */
    running() {
      return this.schemes.find((item) => item.status == 1) || null;
    },
    currentName() {
      if (this.running) return this.running.schemeName;
      if (this.schemes.length) return this.schemes[0].schemeName;
      return this.member.plan || '暂无方案';
    },
    currentDesc() {
      const scheme = this.running || this.schemes[0];
      if (!scheme) return `档案摘要：执行中 ${this.member.runningSchemeCount || 0} 个`;
      const status = SCHEME_STATUS[scheme.status] || '--';
      return `${status} · ${scheme.startDate || '--'} 至 ${scheme.endDate || '--'}`;
    }
  },
  watch: {
    'member.id': {
      immediate: true,
      handler(id) {
        if (id) this.load();
      }
    }
  },
  methods: {
    taskStatusText(status) {
      return TASK_STATUS[status] || '--';
    },
    taskTypeText(type) {
      return TASK_TYPE[type] || '任务';
    },
    load() {
      const memberId = this.member.id;
      this.loading = true;
      Promise.all([
        listMemberSchemes(memberId),
        listTodaySchemeTasks(memberId)
      ]).then(([schemeRes, taskRes]) => {
        this.schemes = schemeRes.data || [];
        this.todayTasks = taskRes.data || [];
        const running = this.schemes.find((item) => item.status == 1);
        if (!running) {
          this.progress = {};
          return null;
        }
        return getSchemeProgress(running.id);
      }).then((progressRes) => {
        if (progressRes) this.progress = progressRes.data || {};
      }).finally(() => {
        this.loading = false;
      });
    },
    openIssue() {
      this.issueVisible = true;
      listAvailableSchemeTemplates(this.member.id).then((res) => {
        this.templates = res.data || [];
        if (!this.templates.length) this.$message.warning('该会员暂无可发放的已发布模板');
      });
    },
    loadTemplateItems(templateId) {
      this.templateItems = [];
      this.issueForm.itemIds = [];
      if (!templateId) return;
      getSchemeTemplateDetail(templateId).then((res) => {
        const items = ((res.data && res.data.items) || []).filter((item) => item.status != 0);
        this.templateItems = items;
        this.issueForm.itemIds = items.map((item) => item.id);
        this.checkAll = true;
      });
    },
    toggleAll(checked) {
      this.issueForm.itemIds = checked ? this.templateItems.map((item) => item.id) : [];
    },
    resetIssue() {
      this.issueForm = { templateId: null, startDate: '', itemIds: [] };
      this.templateItems = [];
      this.templates = [];
      this.checkAll = true;
    },
    submitIssue() {
      if (!this.issueForm.templateId) {
        this.$message.warning('请选择方案模板');
        return;
      }
      if (this.templateItems.length && !this.issueForm.itemIds.length) {
        this.$message.warning('请至少勾选一个任务项');
        return;
      }
      this.issuing = true;
      issueHealthScheme({
        memberId: this.member.id,
        templateId: this.issueForm.templateId,
        itemIds: this.issueForm.itemIds,
        startDate: this.issueForm.startDate || undefined
      }).then(() => {
        this.$message.success('方案已发放');
        this.issueVisible = false;
        this.load();
        this.$emit('changed');
      }).finally(() => {
        this.issuing = false;
      });
    },
    terminate() {
      if (!this.running) return;
      this.$confirm('确认终止当前执行中的方案？', '提示', { type: 'warning' }).then(() => {
        terminateHealthScheme(this.running.id).then(() => {
          this.$message.success('方案已终止');
          this.load();
          this.$emit('changed');
        });
      }).catch(() => {});
    }
  }
};
</script>

<style lang="less" scoped>
.plan-actions {
  display: flex;
  gap: 8px;
  margin-top: 14px;
}

.primary.is-light {
  padding: 8px 12px;
  border: 0;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  font-size: 12px;
  cursor: pointer;
}

.issue-items {
  max-height: 240px;
  overflow: auto;
}

.issue-item-meta {
  margin-left: 6px;
  color: #9ca3af;
  font-style: normal;
}

/deep/ .el-checkbox {
  display: flex;
  align-items: center;
  margin: 8px 0;
}
</style>
